
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useAuthMethods = () => {
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (!error) toast.success("Successfully signed in!");
      return { error };
    } catch (error) {
      console.error('SignIn error:', error);
      return { error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${window.location.origin}/` }
      });
      if (!error) toast.success("Account created! Please check your email to confirm your account.");
      return { error };
    } catch (error) {
      console.error('SignUp error:', error);
      return { error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin }
      });
      return { error };
    } catch (error) {
      console.error('Google SignIn error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      const authKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('sb-') || key.includes('supabase')
      );
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Supabase signOut error:', error);
        
        if (error.message?.includes('Session not found') || error.message?.includes('session')) {
          authKeys.forEach(key => localStorage.removeItem(key));
          toast.success("Successfully signed out.");
          return;
        }
        
        throw error;
      }
      
      authKeys.forEach(key => localStorage.removeItem(key));
      toast.success("Successfully signed out.");
    } catch (error) {
      console.error('SignOut error:', error);
      
      const authKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('sb-') || key.includes('supabase')
      );
      authKeys.forEach(key => localStorage.removeItem(key));
      toast.success("Signed out (cleared local session).");
      throw error;
    }
  };

  const forceSignOut = () => {
    const authKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('sb-') || key.includes('supabase')
    );
    authKeys.forEach(key => localStorage.removeItem(key));
    window.location.reload();
  };

  const deleteAccount = async (password: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { password },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        console.error('Account deletion error:', error);
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return { success: true };
    } catch (error) {
      console.error('Delete account error:', error);
      return { error };
    }
  };

  return { signIn, signUp, signInWithGoogle, signOut, forceSignOut, deleteAccount };
};
